import { Component } from '@theme/component';

/**
 * @typedef {Object} FaqTabsRefs
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLElement[]} [tabLabels]
 * @property {HTMLElement} [tablist]
 * @property {HTMLElement} [tabsBar]
 */

/**
 * Accessible FAQ tabs (ARIA Tabs pattern, automatic activation).
 *
 * Progressive enhancement: on connect the component adds
 * `faq-page__component--enhanced`, which switches the CSS from "all panels
 * visible" (no-JS fallback) to single-panel display. Deep-linking via
 * `#panel-id` is supported through the hash.
 *
 * @extends {Component<FaqTabsRefs>}
 */
class FaqTabs extends Component {
  #abortController = new AbortController();
  #observer = new MutationObserver(() => this.#syncLabels());
  /** rAF handle so a burst of scroll events only triggers one measure. */
  #stuckFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.classList.add('faq-page__component--enhanced');
    this.#syncLabels();
    this.#activateFromHash({ focus: false });
    this.#updateScrollFade();
    this.#updateStuck();

    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
    window.addEventListener('hashchange', this.#onHashChange, { signal: this.#abortController.signal });

    const { signal } = this.#abortController;
    this.refs.tablist?.addEventListener('scroll', this.#onScrollOrResize, { passive: true, signal });
    window.addEventListener('resize', this.#onScrollOrResize, { passive: true, signal });
    window.addEventListener('scroll', this.#onWindowScroll, { passive: true, signal });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncLabels();
    this.#updateScrollFade();
    this.#updateStuck();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    this.#abortController.abort();
    if (this.#stuckFrame) cancelAnimationFrame(this.#stuckFrame);
    this.#stuckFrame = 0;
  }

  #onHashChange = () => this.#activateFromHash({ focus: false });

  #onScrollOrResize = () => {
    this.#updateScrollFade();
    this.#scheduleStuckUpdate();
  };

  #onWindowScroll = () => this.#scheduleStuckUpdate();

  #scheduleStuckUpdate() {
    if (this.#stuckFrame) return;
    this.#stuckFrame = requestAnimationFrame(() => {
      this.#stuckFrame = 0;
      this.#updateStuck();
    });
  }

  /**
   * Tighten the tab bar once it is pinned under the header. The sticky offset is
   * read from the computed `top` (it resolves `--header-height`, which the
   * sticky header updates as it morphs), so the state stays correct whatever the
   * header height is. The bar's own padding doesn't move its static position, so
   * shrinking it can't feed back into the measure.
   */
  #updateStuck() {
    const bar = this.refs.tabsBar;
    if (!bar) return;

    const stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
    const stuck = bar.getBoundingClientRect().top <= stickyTop + 1;

    bar.classList.toggle('faq-page__tabs-bar--stuck', stuck);
  }

  /**
   * Show the edge fade only on the side(s) that still have hidden pills, so no
   * fade appears on the left until the row has actually been scrolled.
   */
  #updateScrollFade() {
    const scroller = this.refs.tablist;
    if (!scroller) return;

    const canScrollLeft = scroller.scrollLeft > 1;
    const canScrollRight = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;

    scroller.style.setProperty('--faq-tabs-fade-start', canScrollLeft ? '3rem' : '0px');
    scroller.style.setProperty('--faq-tabs-fade-end', canScrollRight ? '3rem' : '0px');
  }

  /**
   * Fill each pill's label from its panel's `.faq-category[data-category-name]`.
   * The section can't read dynamic block settings, so the name comes from the
   * server-rendered category markup instead.
   */
  #syncLabels() {
    const { tabs, panels, tabLabels } = this.refs;
    if (!tabs || !panels) return;

    for (let index = 0; index < tabs.length; index++) {
      const panel = panels[index];
      const label = tabLabels?.[index];
      if (!panel || !label) continue;

      const name = panel.querySelector('.faq-category')?.getAttribute('data-category-name')?.trim();
      if (!name || label.textContent?.trim() === name) continue;

      label.textContent = name;
    }
  }

  /** @param {{ focus: boolean }} [options] */
  #activateFromHash({ focus } = { focus: false }) {
    const { tabs } = this.refs;
    if (!tabs || tabs.length === 0) return;

    const hash = location.hash.slice(1);
    const match = hash ? tabs.find((tab) => tab.getAttribute('aria-controls') === hash) : null;
    this.#activateTab(match ?? tabs[0], { focus });
  }

  /**
   * @param {HTMLButtonElement | undefined} tab
   * @param {{ focus?: boolean, updateHash?: boolean }} [options]
   */
  #activateTab(tab, { focus = false, updateHash = false } = {}) {
    const { tabs, panels } = this.refs;
    if (!tab || !tabs || !panels) return;

    const panelId = tab.getAttribute('aria-controls');

    for (const current of tabs) {
      const selected = current === tab;
      current.setAttribute('aria-selected', String(selected));
      current.setAttribute('tabindex', selected ? '0' : '-1');
      current.classList.toggle('faq-page__tab--active', selected);
    }

    for (const panel of panels) {
      panel.classList.toggle('faq-page__panel--active', panel.id === panelId);
    }

    if (focus) tab.focus();
    if (updateHash && panelId) history.replaceState(null, '', `#${panelId}`);
  }

  /**
   * The theme's delegated event system proxies `event.target` to the element
   * carrying the `on:click` attribute (the tab button), while `currentTarget`
   * stays the document — so resolve the button from `target`.
   * @param {Event} event
   */
  handleTabClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tab = /** @type {HTMLButtonElement | null} */ (target.closest('[role="tab"]'));
    if (tab) this.#activateTab(tab, { updateHash: true });
  }

  /** @param {KeyboardEvent} event */
  handleTabKeydown(event) {
    const { tabs } = this.refs;
    if (!tabs || tabs.length === 0) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    const currentTab = target.closest('[role="tab"]');
    const currentIndex = tabs.findIndex((tab) => tab === currentTab);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activateTab(tabs[nextIndex], { focus: true, updateHash: true });
  }
}

if (!customElements.get('faq-tabs')) {
  customElements.define('faq-tabs', FaqTabs);
}
